import React, { useMemo, useRef, useState } from "react";
import { ListTodo, Plus, Eye, EyeOff } from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { NewTask, SubTask } from "../../../types";
import { reorderVisibleInFullList } from "../../../lib/reorderVisibleInFullList";
import { useReorderSubtasks } from "../../../hooks/mutations/useReorderSubtasks";
import SortableSubtaskRow from "./SortableSubtaskRow";

/**
 * A sub-task in an unsaved task. Add mode holds sub-tasks as plain titles in
 * the draft `NewTask` until the parent task exists and they can be persisted.
 */
export type DraftSubtask = NonNullable<NewTask["subtasks"]>[number];

interface EditSubtaskListProps {
  mode: "edit";
  /** Owning task id, stamped onto newly added sub-tasks. */
  taskId: string;
  subtasks: SubTask[];
  onSubtasksChange: (subtasks: SubTask[]) => void;
  onNavigateToSubtask: (subtaskId: string) => void;
}

interface AddSubtaskListProps {
  mode: "add";
  subtasks: DraftSubtask[];
  onSubtasksChange: (subtasks: DraftSubtask[]) => void;
}

export type SubtaskListProps = EditSubtaskListProps | AddSubtaskListProps;

/**
 * The sub-task list inside the task dialog: the add field, the reorderable
 * rows, and the show/hide toggle for completed rows.
 *
 * Owns everything the list needs — the draft title, the completed-visibility
 * flag and the drag sensors — and reports every mutation back as a whole new
 * sub-task array, so the dialog only has to decide where that array lives.
 */
const SubtaskList: React.FC<SubtaskListProps> = (props) => {
  const { mode, subtasks } = props;

  const [newSubTaskTitle, setNewSubTaskTitle] = useState("");
  const [showCompletedSubtasks, setShowCompletedSubtasks] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  const reorderSubtasks = useReorderSubtasks();
  // The dialog builds the change callback around the task it has right now.
  // A failed save must call the latest callback, or it would write an older
  // copy of the task back over edits made while the save was in flight.
  const reportOrderRef = useRef<(next: SubTask[]) => void>(() => {});
  if (props.mode === "edit") {
    reportOrderRef.current = props.onSubtasksChange;
  }

  const hasSubtasks = subtasks.length > 0;
  const completedSubtasks =
    mode === "edit"
      ? subtasks.filter((st) => "completed" in st && st.completed).length
      : 0;

  const toggleSubTask = (subtaskId: string) => {
    if (props.mode !== "edit") return;
    props.onSubtasksChange(
      props.subtasks.map((st) =>
        st.id === subtaskId ? { ...st, completed: !st.completed } : st,
      ),
    );
  };

  const addSubTask = () => {
    if (!newSubTaskTitle.trim()) return;

    if (props.mode === "edit") {
      const newSubTask: SubTask = {
        id: `temp-${Date.now()}`,
        task_id: props.taskId,
        title: newSubTaskTitle.trim(),
        completed: false,
        order_position: 0,
        description: "",
        priority: 4,
        scheduled_date: null,
        scheduled_time: null,
        deadline_date: null,
        duration: null,
        persistent: false,
        updated_at: "",
      };
      props.onSubtasksChange([...props.subtasks, newSubTask]);
    } else {
      props.onSubtasksChange([
        ...props.subtasks,
        { title: newSubTaskTitle.trim() },
      ]);
    }
    setNewSubTaskTitle("");
  };

  const deleteSubTask = (subtaskId: string) => {
    if (props.mode === "edit") {
      props.onSubtasksChange(
        props.subtasks.filter((st) => st.id !== subtaskId),
      );
    } else {
      // Add mode has no ids yet, so rows identify themselves by title and the
      // first match is the one removed.
      const index = props.subtasks.findIndex((st) => st.title === subtaskId);
      props.onSubtasksChange(props.subtasks.filter((_, i) => i !== index));
    }
  };

  const subtaskIds = useMemo(() => {
    if (mode === "edit") {
      return (subtasks as SubTask[])
        .filter((st) => showCompletedSubtasks || !st.completed)
        .map((st) => st.id);
    }
    return subtasks.map((_, i) => `new-subtask-${i}`);
  }, [mode, subtasks, showCompletedSubtasks]);

  const commitEditOrder = (activeId: string, overId: string) => {
    if (props.mode !== "edit") return;
    const previous = props.subtasks;
    const next = reorderVisibleInFullList(
      previous,
      subtaskIds,
      activeId,
      overId,
    );
    if (next === previous) return;
    reportOrderRef.current(next);
    // A row that has not been saved yet has no database id. The later task
    // save writes its place. Reordering only saved rows here keeps that write
    // from failing the whole list.
    if (next.every((st) => !st.id.startsWith("temp-"))) {
      void reorderSubtasks(props.taskId, next).then(
        (saved) => {
          if (!saved) reportOrderRef.current(previous);
        },
        () => {
          reportOrderRef.current(previous);
        },
      );
    }
  };

  const moveSubTask = (index: number, direction: "up" | "down") => {
    if (props.mode !== "add") return;
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= props.subtasks.length) return;
    const items = [...props.subtasks];
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    props.onSubtasksChange(items);
  };

  const moveEditSubtask = (id: string, direction: "up" | "down") => {
    const index = subtaskIds.indexOf(id);
    const target = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= subtaskIds.length) return;
    commitEditOrder(id, subtaskIds[target]);
  };

  const handleSubtaskDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (props.mode === "edit") {
      commitEditOrder(String(active.id), String(over.id));
      return;
    }

    const oldIndex = subtaskIds.indexOf(active.id as string);
    const newIndex = subtaskIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    const items = [...props.subtasks];
    const [moved] = items.splice(oldIndex, 1);
    items.splice(newIndex, 0, moved);
    props.onSubtasksChange(items);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-warm-text-secondary mb-2 flex items-center gap-2">
        <ListTodo size={16} />
        Subtasks
        {hasSubtasks && (
          <span className="text-xs text-warm-text-tertiary font-normal">
            ({completedSubtasks}/{subtasks.length} completed)
          </span>
        )}
      </label>

      {/* Add subtask input */}
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newSubTaskTitle}
          onChange={(e) => setNewSubTaskTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addSubTask()}
          placeholder="Add a subtask..."
          className="flex-1 bg-warm-bg-elevated border border-warm-border rounded-soft px-3 py-2 text-sm text-warm-text-primary placeholder-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue"
        />
        <button
          type="button"
          onClick={addSubTask}
          disabled={!newSubTaskTitle.trim()}
          aria-label="Add sub-task"
          className="px-3 py-2 bg-warm-blue hover:bg-warm-blue-dark disabled:bg-warm-bg-elevated disabled:cursor-not-allowed text-white rounded-soft transition-colors shadow-soft-sm"
        >
          <Plus size={16} />
        </button>
      </div>

      {hasSubtasks && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSubtaskDragEnd}
        >
          <SortableContext
            items={subtaskIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="bg-warm-bg-elevated rounded-soft border border-warm-border overflow-hidden">
              {completedSubtasks > 0 && (
                <div className="flex items-center justify-between px-4 py-2 border-b border-warm-border-subtle bg-warm-bg-card">
                  <span className="text-xs text-warm-text-secondary">
                    {`${completedSubtasks}/${subtasks.length} completed`}
                  </span>
                  <button
                    onClick={() =>
                      setShowCompletedSubtasks(!showCompletedSubtasks)
                    }
                    className="text-warm-text-tertiary hover:text-warm-blue dark:hover:text-warm-blue transition-colors"
                    aria-label={
                      showCompletedSubtasks
                        ? "Hide completed subtasks"
                        : "Show completed subtasks"
                    }
                  >
                    {showCompletedSubtasks ? (
                      <Eye size={14} />
                    ) : (
                      <EyeOff size={14} />
                    )}
                  </button>
                </div>
              )}
              {props.mode === "edit"
                ? props.subtasks
                    .filter((st) => showCompletedSubtasks || !st.completed)
                    .map((subtask, index, filteredSubtasks) => (
                      <SortableSubtaskRow
                        key={subtask.id}
                        id={subtask.id}
                        title={subtask.title}
                        completed={subtask.completed}
                        priority={subtask.priority}
                        scheduledDate={subtask.scheduled_date}
                        scheduledTime={subtask.scheduled_time}
                        tags={subtask.tags}
                        hasAttachments={
                          (subtask.attachments?.length ?? 0) > 0
                        }
                        hasLinks={(subtask.links?.length ?? 0) > 0}
                        isEdit={true}
                        isFirst={index === 0}
                        isLast={index === filteredSubtasks.length - 1}
                        isLastItem={index === filteredSubtasks.length - 1}
                        onToggle={() => toggleSubTask(subtask.id)}
                        onMoveUp={() => moveEditSubtask(subtask.id, "up")}
                        onMoveDown={() => moveEditSubtask(subtask.id, "down")}
                        onDelete={() => deleteSubTask(subtask.id)}
                        onNavigateToSubtask={() =>
                          props.onNavigateToSubtask(subtask.id)
                        }
                      />
                    ))
                : props.subtasks.map((subtask, index, allSubtasks) => (
                    <SortableSubtaskRow
                      key={`new-subtask-${index}`}
                      id={`new-subtask-${index}`}
                      title={subtask.title}
                      isEdit={false}
                      isFirst={index === 0}
                      isLast={index === allSubtasks.length - 1}
                      isLastItem={index === allSubtasks.length - 1}
                      onMoveUp={() => moveSubTask(index, "up")}
                      onMoveDown={() => moveSubTask(index, "down")}
                      onDelete={() => deleteSubTask(subtask.title)}
                    />
                  ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default SubtaskList;
